import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BarChart3,
  Brain,
  Building2,
  CheckCircle2,
  ChevronLeft,
  Heart,
  MapPin,
  Network,
  Search,
  Shield,
  Sparkles,
  Stethoscope,
  Users,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Logo } from '../components/Logo';
import SEOHead from '../components/SEOHead';
import { SEO_IMAGES } from '../utils/seo';

const PRINCIPLES = [
  {
    icon: Search,
    title: 'Busca com intenção',
    body: 'A plataforma considera sintoma, especialidade, cidade, convênio e dados do perfil para reduzir ruído na escolha.',
  },
  {
    icon: Shield,
    title: 'Informação confiável',
    body: 'Organizamos dados públicos e informações cadastradas para que pacientes comparem opções com mais clareza.',
  },
  {
    icon: Heart,
    title: 'Cuidado mais humano',
    body: 'Tecnologia deve diminuir a ansiedade da busca, não substituir avaliação médica ou criar falsas certezas.',
  },
  {
    icon: Network,
    title: 'Ecossistema conectado',
    body: 'Profissionais, clínicas, conteúdo, dados e parceiros trabalham melhor quando a jornada fica mais integrada.',
  },
];

const FLOW = [
  { icon: Brain, title: 'Paciente explica', text: 'Sintoma, necessidade, especialidade ou convênio.' },
  { icon: Stethoscope, title: 'BuscaDoutor organiza', text: 'Filtros, localização, perfis e sinais de compatibilidade.' },
  { icon: CheckCircle2, title: 'Escolha mais clara', text: 'Informações úteis para decidir o próximo passo.' },
];

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Sobre Nós | BuscaDoutor';
  }, []);

  return (
    <div className="min-h-screen bg-[#F7FBF9]" data-testid="about-page">
      <SEOHead
        title="Sobre o BuscaDoutor | Saúde digital com informação confiável"
        description="Conheça o BuscaDoutor, plataforma que organiza informações de médicos, clínicas, especialidades, convênios e atendimento para aproximar pacientes e profissionais de saúde."
        canonicalUrl="/sobre"
        imageUrl={SEO_IMAGES.about}
      />
      <Navbar />

      <section className="relative overflow-hidden border-b border-[#DDE8E4] bg-[#F5FAF8]">
        <div className="container-app py-10 md:py-16">
          <Link
            to="/"
            className="mb-8 inline-flex items-center gap-2 text-sm text-[#59706A] transition-colors hover:text-primary"
            data-testid="about-back-link"
          >
            <ChevronLeft className="h-4 w-4" />
            Voltar
          </Link>

          <div className="grid gap-10 lg:grid-cols-[minmax(0,1.05fr)_minmax(360px,0.95fr)] lg:items-center">
            <div className="max-w-3xl">
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-primary/10 bg-white px-4 py-2 text-sm font-semibold text-primary shadow-sm">
                <Sparkles className="h-4 w-4" />
                Sobre o BuscaDoutor
              </div>

              <h1
                className="text-3xl font-light leading-tight tracking-tight text-[#1A2421] sm:text-4xl md:text-5xl lg:text-6xl"
                style={{ fontFamily: 'Outfit, sans-serif' }}
                data-testid="about-headline"
              >
                Tornamos a busca por cuidado mais simples, transparente e confiável.
              </h1>

              <p className="mt-5 max-w-2xl text-base leading-relaxed text-[#59706A] md:text-lg">
                O BuscaDoutor nasceu para organizar informações de saúde que hoje ficam espalhadas:
                médicos, clínicas, especialidades, convênios, localização e dados de atendimento.
                Nosso papel é reduzir fricção para pacientes e dar mais visibilidade qualificada a
                profissionais e clínicas.
              </p>

              <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                <Link
                  to="/#busca"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-7 py-3 text-sm font-semibold text-white shadow-[0_10px_28px_rgba(28,78,66,0.18)] transition-colors hover:bg-[#2D6D5D]"
                  data-testid="about-cta-patient-top"
                >
                  Buscar atendimento
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/como-funciona-pj"
                  className="inline-flex items-center justify-center rounded-full border border-primary/30 bg-white px-7 py-3 text-sm font-semibold text-primary transition-colors hover:bg-primary/5"
                  data-testid="about-cta-provider-top"
                >
                  Para clínicas e profissionais
                </Link>
              </div>
            </div>

            <div className="rounded-[28px] border border-white bg-white p-4 shadow-[0_18px_55px_rgba(28,78,66,0.12)]">
              <div className="relative overflow-hidden rounded-2xl bg-[#1C4E42] p-5 text-white">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(240,200,106,0.22),transparent_34%),radial-gradient(circle_at_bottom_left,rgba(255,255,255,0.12),transparent_32%)]" />
                <div className="relative">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#F0C86A]">Nossa ponte</p>
                      <h2 className="mt-2 text-xl font-semibold">Entre quem procura e quem cuida</h2>
                    </div>
                    <Logo size="sm" variant="on-dark" showWordmark={false} />
                  </div>

                  <div className="mt-6 grid gap-3">
                    {FLOW.map((item) => {
                      const Icon = item.icon;
                      return (
                        <div key={item.title} className="flex gap-3 rounded-2xl bg-white/10 p-4 backdrop-blur">
                          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-white/12 text-[#F0C86A]">
                            <Icon className="h-5 w-5" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold">{item.title}</p>
                            <p className="mt-0.5 text-xs leading-relaxed text-white/65">{item.text}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="mt-5 grid grid-cols-3 gap-3">
                    {[
                      ['90+', 'especialidades'],
                      ['5mil+', 'cidades'],
                      ['SEO', 'público'],
                    ].map(([number, label]) => (
                      <div key={label} className="rounded-2xl bg-white/10 p-3 text-center">
                        <p className="text-xl font-semibold">{number}</p>
                        <p className="text-[11px] text-white/60">{label}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-12 md:py-16" data-testid="about-mission">
        <div className="container-app">
          <div className="grid gap-8 lg:grid-cols-[0.85fr_1.15fr] lg:items-start">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-primary">Nossa missão</span>
              <h2
                className="mt-3 text-2xl font-semibold leading-tight text-[#1A2421] md:text-4xl"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Reduzir o caminho entre dúvida, informação e decisão.
              </h2>
            </div>
            <div className="grid gap-4 text-[#59706A]">
              <p className="leading-relaxed">
                Procurar atendimento de saúde ainda é difícil: listas desatualizadas, convênios
                confusos, perfis incompletos, pouca transparência e muitas etapas para entender
                quem pode ajudar.
              </p>
              <p className="leading-relaxed">
                O BuscaDoutor trabalha para transformar essa jornada em algo mais objetivo. Para o
                paciente, isso significa encontrar opções com mais contexto. Para o profissional,
                significa ter uma presença digital mais clara, mensurável e preparada para gerar
                confiança.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16" data-testid="about-pillars">
        <div className="container-app">
          <div className="mb-8 max-w-2xl">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-primary">Como pensamos</span>
            <h2
              className="mt-3 text-2xl font-semibold leading-tight text-[#1A2421] md:text-4xl"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Princípios que nos guiam
            </h2>
          </div>

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {PRINCIPLES.map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.title}
                  className="rounded-2xl border border-[#DDE8E4] bg-white p-6 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-[0_14px_38px_rgba(28,78,66,0.08)]"
                  data-testid={`pillar-${item.title.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="text-lg font-semibold text-[#1A2421]">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#59706A]">{item.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-[#F0F7F4] py-12 md:py-16">
        <div className="container-app">
          <div className="grid gap-6 rounded-[28px] bg-white p-6 shadow-[0_16px_45px_rgba(28,78,66,0.08)] md:p-8 lg:grid-cols-2">
            <div>
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <Users className="h-6 w-6" />
              </div>
              <h2 className="text-2xl font-semibold text-[#1A2421]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Para pacientes
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-[#59706A] md:text-base">
                Uma busca mais direta por especialidade, cidade, convênio ou sintoma, com informações
                organizadas para ajudar na escolha do próximo passo.
              </p>
              <div className="mt-5 grid gap-2">
                {['Encontrar especialidades', 'Filtrar por localização', 'Ver convênios e dados do perfil'].map((item) => (
                  <div key={item} className="flex items-center gap-2 text-sm font-medium text-[#315A50]">
                    <CheckCircle2 className="h-4 w-4 text-primary" />
                    {item}
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#FFF4D6] text-amber-800">
                <Building2 className="h-6 w-6" />
              </div>
              <h2 className="text-2xl font-semibold text-[#1A2421]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Para profissionais e clínicas
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-[#59706A] md:text-base">
                Um perfil público mais completo, com recursos de validação, dados de atendimento,
                métricas e ferramentas premium para aumentar conversão com responsabilidade.
              </p>
              <div className="mt-5 grid gap-2">
                {['Reivindicar perfil existente', 'Atualizar dados profissionais', 'Acompanhar demanda e origem dos acessos'].map((item) => (
                  <div key={item} className="flex items-center gap-2 text-sm font-medium text-[#315A50]">
                    <CheckCircle2 className="h-4 w-4 text-primary" />
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-[#1C4E42] py-14 text-white md:py-18" data-testid="about-vision">
        <div className="container-app relative z-10">
          <div className="max-w-3xl">
            <div className="mb-5 inline-flex items-center gap-2">
              <Logo size="sm" variant="on-dark" showWordmark={false} />
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#F0C86A]">Nossa visão</span>
            </div>

            <h2
              className="text-3xl font-light leading-tight md:text-4xl"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Um Brasil onde encontrar cuidado de saúde seja menos confuso e mais orientado por informação.
            </h2>

            <p className="mt-4 max-w-2xl text-base leading-relaxed text-white/70">
              Não substituímos médicos, clínicas ou protocolos de cuidado. Construímos a camada de
              informação e descoberta que ajuda pessoas a encontrarem caminhos melhores.
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/#busca"
                className="inline-flex items-center justify-center rounded-full bg-white px-7 py-3 text-sm font-semibold text-primary transition-colors hover:bg-white/90"
                data-testid="about-cta-patient"
              >
                Buscar agora
              </Link>
              <Link
                to="/como-funciona-pj"
                className="inline-flex items-center justify-center rounded-full border border-white/30 px-7 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/10"
                data-testid="about-cta-provider"
              >
                Sou profissional ou clínica
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
